<template>
    <div class="checkout">
        <div class="checkout_background">
            <div class="checkout_background_pattern"></div>
        </div>

        <div class="checkout_container">
            <!-- Loading State -->
            <div v-if="loadingPlan" class="checkout_loading">
                <div class="checkout_loading_content">
                    <app-sppiner />
                    <h3>Carregando dados do checkout...</h3>
                    <p>Validando sua sessão segura</p>
                </div>
            </div>

            <!-- Checkout Content -->
            <div v-else>
            <!-- Header do Checkout -->
            <div class="checkout_header">
                <!-- Botão Voltar -->
                <div class="checkout_headerBack">
                    <sw-button 
                        name="Voltar" 
                        color="gray" 
                        @click="goBack"
                    >
                        <template #icon>
                            <sw-icon name="arrow-left" />
                        </template>
                    </sw-button>
                </div>
                
                <!-- Security Indicators no Header -->
                <div class="checkout_headerSecurity">
                    <div class="headerSecurity_main">
                        <sw-icon name="shield" />
                        <span>Seus dados estão 100% protegidos</span>
                    </div>
                    <div class="headerSecurity_badges">
                        <div class="securityBadge">
                            <sw-icon name="shield" />
                            <span>SSL 256</span>
                        </div>
                        <div class="securityBadge">
                            <sw-icon name="lock" />
                            <span>PCI DSS</span>
                        </div>
                        <div class="securityBadge">
                            <sw-icon name="checkmark" />
                            <span>Auditado</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="checkout_content">
                <!-- Resumo do Plano -->
                <div class="checkout_summary">
                    <div class="checkoutSummary">
                        <div class="checkoutSummary_header">
                            <h2 class="checkoutSummary_title">Resumo do Pedido</h2>
                            <div class="checkoutSummary_badge" :class="`checkoutSummary_badge--${selectedPlan.id}`">
                                {{ selectedPlan.name }}
                            </div>
                        </div>

                        <!-- Plan & Pricing Hero -->
                        <div class="checkoutSummary_planHero">
                            <div class="planHero_background">
                                <div class="planHero_backgroundPattern"></div>
                            </div>
                            
                            <div class="planHero_content">
                                <!-- Billing Toggle -->
                                <div class="planHero_billingToggle">
                                    <button 
                                        :class="['billingToggle_btn', { active: currentBilling === 'monthly' }]"
                                        @click="changeBilling('monthly')"
                                    >
                                        Mensal
                                    </button>
                                    <button 
                                        :class="['billingToggle_btn', { active: currentBilling === 'yearly' }]"
                                        @click="changeBilling('yearly')"
                                    >
                                        Anual
                                        <span class="billingToggle_discount">-20%</span>
                                    </button>
                                </div>
                                
                                <div class="planHero_header">
                                    <div class="planHero_icon">
                                        <sw-icon :name="selectedPlan.icon" />
                                        <div class="planHero_iconGlow"></div>
                                    </div>
                                    <div class="planHero_info">
                                        <div class="planHero_badge">
                                            <sw-icon name="star-full" />
                                            <span>Plano Escolhido</span>
                                        </div>
                                        <h3 class="planHero_name">{{ selectedPlan.name }}</h3>
                                        <p class="planHero_description">{{ selectedPlan.description }}</p>
                                    </div>
                                </div>
                                
                                <div class="planHero_pricing">
                                    <div class="planHero_pricingHeader">
                                        <div class="planHero_pricingLabel">
                                            <sw-icon name="price-tag" />
                                            <span>{{ selectedPlan.pricing?.billing === 'yearly' ? 'Valor Total Anual' : 'Valor Mensal' }}</span>
                                        </div>
                                        <div class="planHero_price">
                                            <span class="planHero_currency">R$</span>
                                            <span class="planHero_amount">{{ formatPrice(selectedPlan.pricing?.amount || selectedPlan.price || 0) }}</span>
                                            <span class="planHero_period">{{ selectedPlan.pricing?.period || '/mês' }}</span>
                                        </div>
                                    </div>

                                    <!-- Recursos Extras (sempre mensais) -->
                                    <div v-if="planResourcesExtraCost > 0" class="planHero_extraResources">
                                        <div class="extraResources_label">
                                            <sw-icon name="stats-bars" />
                                            <span>Recursos Extras</span>
                                        </div>
                                        <div class="extraResources_cost">
                                            +R$ {{ formatPrice(planResourcesExtraCost) }}/mês
                                        </div>
                                    </div>
                                    
                                    <!-- Informações adicionais para plano anual -->
                                    <div v-if="selectedPlan.pricing?.billing === 'yearly'" class="planHero_yearlyDetails">
                                        <div v-if="selectedPlan.pricing?.discount > 0" class="planHero_discount">
                                            <sw-icon name="gift" />
                                            <span>Desconto de {{ selectedPlan.pricing.discountPercentage }}%: <strong>-R$ {{ formatPrice(selectedPlan.pricing.discount) }}</strong></span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="planHero_commitment">
                                    <sw-icon name="shield" />
                                    <span>Sem compromisso • Cancele quando quiser</span>
                                </div>
                            </div>
                        </div>

                        <!-- Plan Resources Component -->
                        <PlanResources
                            :plan-id="selectedPlan.id"
                            :initial-resources="planResources"
                            :base-resources="baseResources"
                            :storage-max="selectedPlan.id === 'essential' ? 200 : 1000"
                            @update:resources="handleResourcesUpdate"
                            @update:extra-cost="handleExtraCostUpdate"
                        />


                        <div class="checkoutSummary_features">
                            <div class="checkoutSummary_featuresTitle">Incluso no plano:</div>
                            <ul>
                                <li v-for="feature in selectedPlan.features" :key="feature.name" 
                                    :class="['checkoutSummary_featureItem', { 
                                        'checkoutSummary_featureItem--disabled': !feature.included,
                                        'checkoutSummary_featureItem--highlight': feature.highlight 
                                    }]">
                                    <div class="checkoutSummary_featureIcon" 
                                         :class="{ 'checkoutSummary_featureIcon--disabled': !feature.included }">
                                        <sw-icon :name="feature.included ? feature.icon : 'cross'" />
                                    </div>
                                    <div class="checkoutSummary_featureContent">
                                        <span class="checkoutSummary_featureName">{{ feature.name }}</span>
                                        <p class="checkoutSummary_featureDescription" v-if="feature.description">
                                            {{ feature.description }}
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>

                <!-- Formulário de Checkout -->
                <div class="checkout_form">
                    <div class="checkoutForm">
                        <h2 class="checkoutForm_title">
                            <div class="checkoutForm_titleIcon">
                                <sw-icon name="credit-card" />
                            </div>
                            Dados de Pagamento
                            <span class="checkoutForm_titleSparkle">✨</span>
                        </h2>

                        <!-- Indicador de Progresso -->
                        <div class="checkout_steps">
                            <div class="checkout_steps_container">
                                <div class="checkout_step" :class="{ 'checkout_step--active': formStep >= 1, 'checkout_step--completed': formStep > 1 }">
                                    <div class="checkout_step_circle">
                                        <sw-icon v-if="formStep > 1" name="check" />
                                        <span v-else>1</span>
                                    </div>
                                    <div class="checkout_step_label">Informações Pessoais</div>
                                </div>
                                
                                <div class="checkout_steps_divider" :class="{ 'checkout_steps_divider--active': formStep > 1 }"></div>
                                
                                <div class="checkout_step" :class="{ 'checkout_step--active': formStep >= 2 }">
                                    <div class="checkout_step_circle">
                                        <span>2</span>
                                    </div>
                                    <div class="checkout_step_label">Dados do Cartão</div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 1: Informações Pessoais -->
                        <div class="checkout_step_content" v-if="formStep === 1">
                            <div class="checkoutForm_section">
                                <div class="checkout_info_note">
                                    <sw-icon name="user" />
                                    <span>Precisamos desses dados para processar seu pagamento com segurança</span>
                                </div>
                                
                                <div class="checkoutForm_grid">
                                    <div class="checkoutForm_field">
                                        <sw-input
                                            v-model="customerData.name"
                                            label="Nome Completo"
                                            placeholder="Digite seu nome completo"
                                            :validation="validation.name"
                                            @blur="validateField('name')"
                                        />
                                    </div>
                                    <div class="checkoutForm_field">
                                        <sw-input
                                            v-model="customerData.email"
                                            type="email"
                                            label="E-mail"
                                            placeholder="Digite seu e-mail"
                                            :validation="validation.email"
                                            @blur="validateField('email')"
                                        />
                                    </div>
                                    <div class="checkoutForm_field">
                                        <sw-input
                                            v-model="customerData.document"
                                            label="CPF/CNPJ"
                                            mask="###.###.###-##"
                                            placeholder="000.000.000-00"
                                            :validation="validation.document"
                                            @blur="validateField('document')"
                                        />
                                    </div>
                                    <div class="checkoutForm_field">
                                        <sw-input
                                            v-model="customerData.phone"
                                            label="Telefone"
                                            mask="(##) #####-####"
                                            placeholder="(11) 99999-9999"
                                            :validation="validation.phone"
                                            @blur="validateField('phone')"
                                        />
                                    </div>
                                </div>

                                <div class="checkout_step_actions">
                                    <sw-button 
                                        name="Continuar para Dados do Cartão"
                                        color="blue"
                                        size="lg"
                                        @click="nextStep"
                                        class="checkout_next_btn"
                                    >
                                        <template #suffix>
                                            <sw-icon name="arrow-right" />
                                        </template>
                                    </sw-button>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Dados do Cartão -->
                        <div class="checkout_step_content" v-if="formStep === 2">
                            <div class="checkoutForm_section" v-if="paymentMethod === 'credit_card'">
                                <div class="checkout_info_note">
                                    <sw-icon name="credit-card" />
                                    <span>Insira os dados do seu cartão com segurança</span>
                                </div>
                            
                            <!-- Cartão Visual -->
                            <div class="credit_card_preview">
                                <div class="credit_card" :class="{ 'flipped': showCardBack }">
                                    <!-- Frente do Cartão -->
                                    <div class="credit_card_front">
                                        <div class="card_gradient"></div>
                                        <div class="card_overlay"></div>
                                        
                                        <!-- Padrão de fundo -->
                                        <div class="card_pattern">
                                            <div class="pattern_circle circle1"></div>
                                            <div class="pattern_circle circle2"></div>
                                            <div class="pattern_circle circle3"></div>
                                        </div>
                                        
                                        <div class="card_content">
                                            <!-- Header Premium -->
                                            <div class="card_header">
                                                <div class="card_brand" :class="getCardBrand(cardData.number)">
                                                    <span v-if="getCardBrand(cardData.number) === 'mastercard'">Mastercard</span>
                                                    <span v-else-if="getCardBrand(cardData.number) === 'visa'">VISA</span>
                                                    <span v-else-if="getCardBrand(cardData.number) === 'elo'">Elo</span>
                                                    <span v-else>Premium Card</span>
                                                </div>
                                                <div class="card_type">PLATINUM</div>
                                            </div>
                                            
                                            <!-- Chip Premium -->
                                            <div class="card_chip_premium">
                                                <div class="chip_inner">
                                                    <div class="chip_reflection"></div>
                                                </div>
                                            </div>
                                            
                                            <!-- Número do Cartão -->
                                            <div class="card_number_premium">
                                                {{ formatCardNumber(cardData.number) }}
                                            </div>
                                            
                                            <!-- Informações do Cartão -->
                                            <div class="card_info">
                                                <div class="card_holder_info">
                                                    <div class="info_label">PORTADOR DO CARTÃO</div>
                                                    <div class="info_value">{{ cardData.holderName || 'SEU NOME AQUI' }}</div>
                                                </div>
                                                <div class="card_expiry_info">
                                                    <div class="info_label">VÁLIDO ATÉ</div>
                                                    <div class="info_value">{{ cardData.expiry || '12/29' }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Elementos de segurança -->
                                        <div class="security_elements">
                                            <div class="hologram"></div>
                                        </div>
                                    </div>

                                    <!-- Verso do Cartão -->
                                    <div class="credit_card_back">
                                        <div class="card_gradient_back"></div>
                                        
                                        <div class="card_content_back">
                                            <!-- Faixa magnética premium -->
                                            <div class="magnetic_stripe_premium"></div>
                                            
                                            <!-- Área de assinatura -->
                                            <div class="signature_area">
                                                <div class="signature_label">Assinatura do Portador</div>
                                                <div class="signature_box">
                                                    <span class="signature_text">{{ cardData.holderName || 'Sua Assinatura' }}</span>
                                                </div>
                                            </div>
                                            
                                            <!-- CVV Premium -->
                                            <div class="cvv_section">
                                                <div class="cvv_container">
                                                    <div class="cvv_label">CVC</div>
                                                    <div class="cvv_value">{{ cardData.cvv || '123' }}</div>
                                                </div>
                                                <div class="cvv_info">
                                                    <p>Código de segurança</p>
                                                    <p>Use apenas em transações seguras</p>
                                                </div>
                                            </div>
                                            
                                            <!-- Informações do banco -->
                                            <div class="bank_info">
                                                <div class="bank_logo">FileSave Bank</div>
                                                <div class="bank_contact">SAC: 0800 123 4567</div>
                                            </div>
                                            
                                            <!-- Selos de segurança -->
                                            <div class="security_seals">
                                                <span class="seal">🔒 SSL</span>
                                                <span class="seal">🛡️ SECURE</span>
                                                <span class="seal">✓ VERIFIED</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="checkoutForm_grid">
                                <div class="checkoutForm_field checkoutForm_field--full">
                                    <sw-input
                                        v-model="cardData.number"
                                        label="Número do Cartão"
                                        mask="#### #### #### ####"
                                        placeholder="1234 5678 9012 3456"
                                        :validation="validation.cardNumber"
                                        @blur="validateField('cardNumber')"
                                    />
                                </div>
                                <div class="checkoutForm_field">
                                    <sw-input
                                        v-model="cardData.holderName"
                                        label="Nome no Cartão"
                                        placeholder="Nome como está no cartão"
                                        :validation="validation.cardName"
                                        @blur="validateField('cardName')"
                                    />
                                </div>
                                <div class="checkoutForm_field">
                                    <sw-input
                                        v-model="cardData.expiry"
                                        label="Validade"
                                        mask="##/##"
                                        placeholder="MM/AA"
                                        :validation="validation.cardExpiry"
                                        @blur="validateField('cardExpiry')"
                                    />
                                </div>
                                <div class="checkoutForm_field">
                                    <sw-input
                                        v-model="cardData.cvv"
                                        label="CVV"
                                        mask="###"
                                        placeholder="123"
                                        :validation="validation.cardCvv"
                                        @focus="flipCardToBack"
                                        @blur="flipCardToFront"
                                    />
                                </div>

                                <div class="checkout_step_actions">
                                    <sw-button
                                        name="Confirmar Pagamento"
                                        color="blue"
                                        size="lg"
                                        :loading="processing"
                                        @click="processPayment"
                                        class="checkout_payment_btn"
                                    >
                                        <template #suffix>
                                            <sw-icon name="credit-card" />
                                        </template>
                                    </sw-button>

                                    <sw-button
                                        name="Voltar"
                                        color="gray"
                                        @click="prevStep"
                                        class="checkout_prev_btn"
                                    >
                                        <template #prefix>
                                            <sw-icon name="arrow-left" />
                                        </template>
                                    </sw-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de Processamento -->
        <sw-modal 
            id="modal_processingModal"
            :visible="showProcessingModal" 
            @update="() => {}"
        >
            <template #content>
                <div class="processingModal">
                    <div class="processingModal_icon">
                        <app-sppiner />
                    </div>
                    <div class="processingModal_content">
                        <h3>Processando Pagamento</h3>
                        <p>Aguarde enquanto processamos seu pagamento de forma segura...</p>
                        <div class="processingModal_steps">
                            <div class="processingStep" :class="{ 'processingStep--active': currentStep >= 1 }">
                                <span class="processingStep_number">1</span>
                                <span>Validando dados</span>
                            </div>
                            <div class="processingStep" :class="{ 'processingStep--active': currentStep >= 2 }">
                                <span class="processingStep_number">2</span>
                                <span>Processando pagamento</span>
                            </div>
                            <div class="processingStep" :class="{ 'processingStep--active': currentStep >= 3 }">
                                <span class="processingStep_number">3</span>
                                <span>Finalizando</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Sucesso -->
        <sw-modal 
            id="modal_successModal"
            :visible="showSuccessModal" 
            @update="closeSuccessModal"
        >
            <template #header>
                <ModalHeader
                    icon="checkmark"
                    title="Pagamento Aprovado!"
                    subtitle="Sua assinatura foi ativada com sucesso"
                    color="green"
                />
            </template>
            
            <template #content>
                <div class="modalContent">
                    <div class="successContent">
                        <div class="successContent_info">
                            <p>Parabéns! Seu pagamento foi processado com sucesso e sua conta foi atualizada para o plano <strong>{{ selectedPlan.name }}</strong>.</p>
                            
                            <div class="successContent_details">
                                <div class="successDetail">
                                    <label>Plano:</label>
                                    <span>{{ selectedPlan.name }}</span>
                                </div>
                                <div class="successDetail">
                                    <label>Valor:</label>
                                    <span>{{ finalPrice }}</span>
                                </div>
                                <div class="successDetail" v-if="paymentResult.transactionId">
                                    <label>ID da Transação:</label>
                                    <span>{{ paymentResult.transactionId }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="modalActions">
                        <sw-button 
                            name="Ir para Dashboard" 
                            color="blue" 
                            @click="goToDashboard" 
                        />
                    </div>
                </div>
            </template>
        </sw-modal>
        </div>
    </div>
    </div>
</template>

<script setup>
	import { ref, computed, onMounted, nextTick } from 'vue';
	import { useRouter, useRoute } from 'vue-router';
	import { notify, loadingFull } from 'sysweb-vue-components/src/global';

	import { 
		processPayment as apiProcessPayment, 
		validateCheckoutSlug,
		invalidateCheckoutSlug,
		preparePaymentData
	} from '@/services/mockApi';
	import PlanResources from '@Component/PlanResources.vue';
import ModalHeader from '@Component/modals/ModalHeader';

	const router = useRouter();
	const route = useRoute();

	// Estados principais
	const processing = ref(false);
	const showProcessingModal = ref(false);
	const showSuccessModal = ref(false);
	const currentStep = ref(0);
	const formStep = ref(1); // Controla os steps do formulário de pagamento
	const paymentMethod = ref('credit_card');
	const showCardBack = ref(false);
	const loadingPlan = ref(true);
	const currentBilling = ref('monthly');
	const isLoadingData = ref(false);

	// Dados do plano (carregados via API)
	const selectedPlan = ref({});
	const sessionInfo = ref({});

	// Recursos customizáveis do plano
	const planResources = ref({
		storage: 10,
		sharingPages: 10,
		uploadPages: 10,
		emailsSent: 100,
		alerts: 20
	});

	// Custo extra dos recursos
	const planResourcesExtraCost = ref(0);

	// Estados de desconto
	const discount = ref({
		active: false,
		label: '',
		value: '',
		percentage: 0
	});

	// Dados do cliente
	const customerData = ref({
		name: '',
		email: '',
		document: '',
		phone: ''
	});

	// Dados do cartão
	const cardData = ref({
		number: '',
		holderName: '',
		expiry: '',
		cvv: '',
	});

	// Resultado do pagamento
	const paymentResult = ref({});

	// Validações
	const validation = ref({
		name: { isValid: true, message: '' },
		email: { isValid: true, message: '' },
		document: { isValid: true, message: '' },
		phone: { isValid: true, message: '' },
		cardNumber: { isValid: true, message: '' },
		cardName: { isValid: true, message: '' },
		cardExpiry: { isValid: true, message: '' },
		cardCvv: { isValid: true, message: '' }
	});


	// Utilitários
	const formatPrice = (price) => {
		if (typeof price !== 'number') {
			price = parseFloat(price) || 0;
		}
		return price.toFixed(2).replace('.', ',');
	};

	// Computed
	const baseResources = computed(() => {
		if (!selectedPlan.value.id) return { storage: 10, sharingPages: 10, uploadPages: 10, emailsSent: 100, alerts: 20 };
		
		return selectedPlan.value.id === 'essential' 
			? { storage: 10, sharingPages: 10, uploadPages: 10, emailsSent: 100, alerts: 20 }
			: { storage: 100, sharingPages: 20, uploadPages: 20, emailsSent: 500, alerts: 100 };
	});

	const finalPrice = computed(() => {
		if (selectedPlan.value.pricing) {
			// Mostrar apenas o preço base do plano, recursos extras são mostrados separadamente
			return `R$ ${formatPrice(selectedPlan.value.pricing.amount)}`;
		}
		return selectedPlan.value.price || 'R$ 0,00';
	});


	// Métodos para recursos
	const handleResourcesUpdate = (resources) => {
		planResources.value = resources;
		updatePricingWithResources();
	};

	const handleExtraCostUpdate = (cost) => {
		planResourcesExtraCost.value = cost;
		updatePricingWithResources();
	};

	const updatePricingWithResources = () => {
		// Não modifica o preço base do plano, recursos extras são mostrados separadamente
		// O preço base permanece inalterado para clareza
	};

	// Funções de navegação entre steps
	const nextStep = () => {
		if (formStep.value === 1 && validatePersonalData()) {
			formStep.value = 2;
		}
	};

	const prevStep = () => {
		if (formStep.value > 1) {
			formStep.value = 1;
		}
	};

	const validatePersonalData = () => {
		validateField('name');
		validateField('email');
		validateField('document');
		validateField('phone');
		
		return !validation.value.name.hasError && 
		       !validation.value.email.hasError && 
		       !validation.value.document.hasError && 
		       !validation.value.phone.hasError;
	};

	// Funções para o cartão visual
	const formatCardNumber = (number) => {
		if (!number) number = '';
		// Remove espaços e caracteres especiais
		const cleaned = number.replace(/\D/g, '');
		
		// Cria um array de 16 posições (4 grupos de 4 dígitos)
		const positions = new Array(16).fill('*');
		
		// Substitui asteriscos pelos dígitos digitados
		for (let i = 0; i < cleaned.length && i < 16; i++) {
			positions[i] = cleaned[i];
		}
		
		// Agrupa em blocos de 4 e junta com espaços
		const groups = [];
		for (let i = 0; i < 16; i += 4) {
			groups.push(positions.slice(i, i + 4).join(''));
		}
		
		return groups.join(' ');
	};

	const getCardBrand = (number) => {
		if (!number) return '';
		const cleaned = number.replace(/\D/g, '');
		
		// Visa: começa com 4
		if (/^4/.test(cleaned)) return 'visa';
		// Mastercard: começa com 5 ou números entre 2221-2720
		if (/^5[1-5]/.test(cleaned) || /^2(22[1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)/.test(cleaned)) return 'mastercard';
		// Elo: vários ranges específicos
		if (/^(4011|4312|4389|4514|4573|5041|5066|5067|6277|6362|6363|6504|6505|6516|6550)/.test(cleaned)) return 'elo';
		
		return '';
	};

	// Métodos de validação
	const validateField = (field) => {
		switch (field) {
			case 'name':
				validation.value.name.isValid = customerData.value.name.length >= 3;
				validation.value.name.message = validation.value.name.isValid ? '' : 'Nome deve ter pelo menos 3 caracteres';
				break;
			case 'email':
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				validation.value.email.isValid = emailRegex.test(customerData.value.email);
				validation.value.email.message = validation.value.email.isValid ? '' : 'E-mail inválido';
				break;
			case 'document':
				validation.value.document.isValid = customerData.value.document.length >= 11;
				validation.value.document.message = validation.value.document.isValid ? '' : 'CPF/CNPJ inválido';
				break;
			case 'phone':
				validation.value.phone.isValid = customerData.value.phone.length >= 10;
				validation.value.phone.message = validation.value.phone.isValid ? '' : 'Telefone inválido';
				break;
			case 'cardNumber':
				validation.value.cardNumber.isValid = cardData.value.number.replace(/\s/g, '').length >= 16;
				validation.value.cardNumber.message = validation.value.cardNumber.isValid ? '' : 'Número do cartão inválido';
				break;
			case 'cardName':
				validation.value.cardName.isValid = cardData.value.holderName.length >= 3;
				validation.value.cardName.message = validation.value.cardName.isValid ? '' : 'Nome no cartão inválido';
				break;
			case 'cardExpiry':
				const expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
				validation.value.cardExpiry.isValid = expiryRegex.test(cardData.value.expiry);
				validation.value.cardExpiry.message = validation.value.cardExpiry.isValid ? '' : 'Data de validade inválida';
				break;
			case 'cardCvv':
				validation.value.cardCvv.isValid = cardData.value.cvv.length >= 3;
				validation.value.cardCvv.message = validation.value.cardCvv.isValid ? '' : 'CVV inválido';
				break;
		}
	};

	// Métodos para controlar o flip do cartão
	const flipCardToBack = () => {
		showCardBack.value = true;
	};

	const flipCardToFront = () => {
		showCardBack.value = false;
		validateField('cardCvv');
	};

	const validateForm = () => {
		// Validar dados do cliente
		validateField('name');
		validateField('email');
		validateField('document');
		validateField('phone');

		// Validar dados do cartão se necessário
		if (paymentMethod.value === 'credit_card') {
			validateField('cardNumber');
			validateField('cardName');
			validateField('cardExpiry');
			validateField('cardCvv');
		}

		// Verificar se há erros
		const hasErrors = Object.values(validation.value).some(field => !field.isValid);
		return !hasErrors;
	};

	const processPayment = async () => {
		if (!validateForm()) {
			alert('Por favor, corrija os campos inválidos antes de continuar.');
			return;
		}

		try {
			processing.value = true;
			showProcessingModal.value = true;
			currentStep.value = 1;

			// Simular steps de processamento
			setTimeout(() => { currentStep.value = 2; }, 1500);
			setTimeout(() => { currentStep.value = 3; }, 3000);

			// Prepara dados específicos do método de pagamento
			let methodData = {};
			if (paymentMethod.value === 'credit_card') {
				methodData = cardData.value;
			} else if (paymentMethod.value === 'pix') {
				methodData = {}; // PIX não precisa de dados extras
			}

			// Prepara dados de pagamento via API mock
			const paymentDataResponse = await preparePaymentData(
				route.params.slug,
				customerData.value,
				paymentMethod.value,
				methodData,
				discount.value.active ? discount.value : null,
				{
					resources: planResources.value,
					extraCost: planResourcesExtraCost.value
				}
			);

			if (!paymentDataResponse.success) {
				notify(`Erro ao preparar pagamento: ${paymentDataResponse.error.message}`, 'error');
				processing.value = false;
				showProcessingModal.value = false;
				currentStep.value = 0;
				return;
			}

			const response = await apiProcessPayment(paymentDataResponse.data);

			setTimeout(async () => {
				showProcessingModal.value = false;
				
				if (response.status === 'success') {
					// Invalida a sessão após pagamento bem-sucedido
					try {
						await invalidateCheckoutSlug(route.params.slug);
					} catch (error) {
						console.warn('Erro ao invalidar sessão:', error);
					}
					
					paymentResult.value = response.data;
					showSuccessModal.value = true;
				} else {
					notify(`Erro no pagamento: ${response.error.message}`, 'error');
				}
				
				processing.value = false;
				currentStep.value = 0;
			}, 4000);

		} catch (error) {
			console.error('Erro no processamento:', error);
			alert('Erro no processamento do pagamento. Tente novamente.');
			processing.value = false;
			showProcessingModal.value = false;
			currentStep.value = 0;
		}
	};

	const closeSuccessModal = () => {
		showSuccessModal.value = false;
	};

	const goToDashboard = () => {
		router.push({ name: 'Dashboard' });
	};

	const goBack = () => {
		// Verifica se há histórico para voltar
		if (window.history.length > 1) {
			router.go(-1);
		} else {
			// Se não há histórico, vai para o dashboard
			router.push({ name: 'Dashboard' });
		}
	};

	// Função para mudar o ciclo de cobrança localmente
	const changeBilling = (newBilling) => {
		if (newBilling === currentBilling.value) return;

		// Calcula novo preço baseado no plano original
		const originalMonthlyPrice = selectedPlan.value.originalPrice;
		const planDiscount = selectedPlan.value.id === 'essential' ? 0.20 : 0.20; // 20% para ambos os planos
		
		let newPricing;
		
		if (newBilling === 'monthly') {
			newPricing = {
				amount: originalMonthlyPrice,
				originalAmount: originalMonthlyPrice,
				discount: 0,
				discountPercentage: 0,
				billing: 'monthly',
				period: '/mês'
			};
		} else {
			const totalWithoutDiscount = originalMonthlyPrice * 12;
			const discountAmount = totalWithoutDiscount * planDiscount;
			const totalWithDiscount = totalWithoutDiscount - discountAmount;
			
			newPricing = {
				amount: totalWithDiscount,
				monthlyEquivalent: originalMonthlyPrice,
				originalAmount: totalWithoutDiscount,
				discount: discountAmount,
				discountPercentage: Math.round(planDiscount * 100),
				billing: 'yearly',
				period: '/ano'
			};
		}

		// Atualiza os dados localmente
		currentBilling.value = newBilling;
		selectedPlan.value.billing = newBilling;
		selectedPlan.value.pricing = newPricing;
		selectedPlan.value.price = `R$ ${formatPrice(newPricing.amount)}`;
	};

	// Carrega dados do plano via slug
	const loadPlanData = async () => {
		// Evitar execução dupla
		if (isLoadingData.value) {
			return;
		}
		
		isLoadingData.value = true;
		
		const slug = route.params.slug;
		
		if (!slug) {
			notify('Sessão inválida', 'error');
			router.push({ name: 'PlanUpgrade' });
			isLoadingData.value = false;
			return;
		}

		// Se é um slug temporário, usar dados do sessionStorage
		if (slug.startsWith('temp_')) {
			const sessionData = sessionStorage.getItem(`checkout_data_${slug}`);
			
			if (sessionData) {
				try {
					const { planData, resources, extraCost } = JSON.parse(sessionData);
					
					// Calcular preço correto baseado no billing cycle
					const originalMonthlyPrice = planData.originalPrice;
					let finalPricing;
					
					if (planData.billing === 'yearly') {
						const planDiscount = 0.20; // 20% desconto
						const totalWithoutDiscount = originalMonthlyPrice * 12;
						const discountAmount = totalWithoutDiscount * planDiscount;
						const totalWithDiscount = totalWithoutDiscount - discountAmount;
						
						finalPricing = {
							amount: totalWithDiscount,
							originalAmount: totalWithoutDiscount,
							discount: discountAmount,
							discountPercentage: 20,
							billing: 'yearly',
							period: '/ano'
						};
					} else {
						finalPricing = {
							amount: originalMonthlyPrice,
							originalAmount: originalMonthlyPrice,
							discount: 0,
							discountPercentage: 0,
							billing: 'monthly',
							period: '/mês'
						};
					}
					
					selectedPlan.value = {
						id: planData.id,
						name: planData.name,
						price: `R$ ${formatPrice(finalPricing.amount)}`,
						pricing: finalPricing,
						description: planData.description,
						icon: planData.icon,
						features: planData.features,
						billing: planData.billing,
						originalPrice: originalMonthlyPrice,
						featured: planData.featured
					};

					// Recursos do sessionStorage
					planResources.value = {
						storage: resources.storage,
						sharingPages: resources.sharingPages,
						uploadPages: resources.uploadPages,
						emailsSent: resources.emailsSent,
						alerts: resources.alerts
					};

					planResourcesExtraCost.value = extraCost || 0;
					currentBilling.value = planData.billing;
					
					// Limpa os dados temporários
					sessionStorage.removeItem(`checkout_data_${slug}`);
					
					loadingPlan.value = false;
					isLoadingData.value = false;
					return;
				} catch (error) {
					console.error('Erro ao carregar dados do sessionStorage:', error);
				}
			}
			
			// Fallback: carregar do mockData usando o planId do slug
			const planId = slug.split('_')[1]; // temp_essential_123 -> essential
			
			// Fallback SPA instantâneo - import direto
			try {
				const { mockPlans } = await import('@/data/mockData');
				const planData = mockPlans.find(p => p.id === planId);
				if (planData) {
					
					selectedPlan.value = {
						id: planData.id,
						name: planData.name,
						price: `R$ ${planData.price.monthly.toFixed(2).replace('.', ',')}`,
						pricing: {
							amount: planData.price.monthly,
							billing: 'monthly'
						},
						description: planData.description,
						icon: planData.icon,
						features: planData.features,
						billing: 'monthly',
						originalPrice: planData.price.monthly,
						featured: planData.featured
					};

					// Recursos padrão do plano
					planResources.value = {
						storage: planData.resources.storage,
						sharingPages: planData.resources.sharingPages,
						uploadPages: planData.resources.uploadPages,
						emailsSent: planData.resources.emailsSent,
						alerts: planData.resources.alerts
					};

					planResourcesExtraCost.value = 0;
					currentBilling.value = 'monthly';
					loadingPlan.value = false;
					isLoadingData.value = false;
					return;
				}
			} catch (error) {
				console.error('Erro ao carregar mockData:', error);
				notify('Erro ao carregar dados do plano', 'error');
				router.push({ name: 'PlanUpgrade' });
				isLoadingData.value = false;
				return;
			}
		}

		try {
			const response = await validateCheckoutSlug(slug);
			
			if (response.status === 'success') {
				const planData = response.data;
				
				selectedPlan.value = {
					id: planData.id,
					name: planData.name,
					price: planData.pricing ? `R$ ${formatPrice(planData.pricing.amount)}` : `R$ ${planData.price?.toFixed(2).replace('.', ',') || '0,00'}`,
					pricing: planData.pricing,
					description: planData.description,
					icon: planData.icon,
					features: planData.features, // Já vem estruturado com name, description, icon, etc
					billing: planData.billing,
					originalPrice: planData.originalPrice,
					featured: planData.featured
				};
				
				// Inicializar recursos baseados nos dados persistidos ou padrão do plano
				planResources.value = planData.resources || (planData.id === 'essential' 
					? { storage: 10, sharingPages: 10, uploadPages: 10, emailsSent: 100, alerts: 20 }
					: { storage: 100, sharingPages: 20, uploadPages: 20, emailsSent: 500, alerts: 100 });

				// Inicializar custo extra dos recursos
				planResourcesExtraCost.value = planData.resourcesExtraCost || 0;
				
				// Atualiza o billing atual
				currentBilling.value = planData.billing;
				
				sessionInfo.value = planData.sessionInfo;
				loadingPlan.value = false;
				isLoadingData.value = false;
				
			} else {
				if (response.error.code === 404) {
					notify('Sessão de checkout não encontrada', 'error');
				} else if (response.error.code === 410) {
					notify('Sessão de checkout expirada', 'error');
				} else {
					notify('Erro ao validar sessão', 'error');
				}
				router.push({ name: 'PlanUpgrade' });
				isLoadingData.value = false;
			}
			
		} catch (error) {
			notify('Erro ao carregar dados do plano', 'error');
			console.error('Erro ao validar slug:', error);
			router.push({ name: 'PlanUpgrade' });
			isLoadingData.value = false;
		}
	};

	// Função para forçar scroll ao topo do container dashboard
	const forceScrollToTop = () => {
		// Buscar o container do dashboard que tem overflow-y: auto
		const dashboardContainer = document.querySelector('.dashboard_container');
		
		if (dashboardContainer) {
			// Scroll imediato primeiro
			dashboardContainer.scrollTop = 0;
			
			// Scroll suave como backup
			setTimeout(() => {
				dashboardContainer.scrollTo({
					top: 0,
					left: 0,
					behavior: 'smooth'
				});
			}, 100);
		} else {
			// Fallback para window se não encontrar o container
			window.scrollTo(0, 0);
		}
	};

	onMounted(async () => {
		// Scroll inicial imediato para o container correto
		forceScrollToTop();
		
		// Carregar dados do plano primeiro
		await loadPlanData();

		// Carregar dados do usuário se disponível
		const savedUser = localStorage.getItem('user');
		if (savedUser) {
			const user = JSON.parse(savedUser);
			customerData.value.name = user.name || '';
			customerData.value.email = user.email || '';
		}

		// Forçar scroll novamente após todos os dados carregados
		await nextTick();
		forceScrollToTop();
	});
</script>

<style lang="scss" scoped>
    .checkout {
        min-height: 100vh;
        background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #ffffff 100%);
        position: relative;
        overflow-x: hidden;
    }

    .checkout_background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0.1;

        &_pattern {
            width: 100%;
            height: 100%;
            background-image: 
                radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.08) 2px, transparent 2px),
                radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.06) 1px, transparent 1px);
            background-size: 80px 80px;
            background-position: 0 0, 40px 40px;
        }
    }

    .checkout_container {
        position: relative;
        z-index: 1;
        max-width: 1400px;
        margin: 0 auto;
        padding: 2em;
    }

    .checkout_header {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 2em;
        margin-bottom: 3em;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
        padding: 2em 2.5em;
        border-radius: 1.5em;
        border: 1px solid rgba(255, 255, 255, 0.8);
        box-shadow: 
            0 10px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(10px);
    }

    .checkout_headerBack {
        transition: all 0.3s ease;

        &:hover {
            transform: translateX(-3px);
        }
    }

    .checkout_headerSecurity {
        justify-self: end;
    }

    .headerSecurity_main {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5em;
        margin-bottom: 0.75em;
        font-weight: 600;
        font-size: 0.9em;
        color: #059669;

        .sw-icon {
            font-size: 1.1em;
            color: #10b981;
            animation: pulse 2s infinite;
        }
    }

    .headerSecurity_badges {
        display: flex;
        justify-content: flex-end;
        gap: 0.75em;
    }

    .securityBadge {
        display: flex;
        align-items: center;
        gap: 0.35em;
        background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
        color: #059669;
        padding: 0.4em 0.75em;
        border-radius: 0.75em;
        font-weight: 600;
        font-size: 0.7em;
        border: 1px solid rgba(16, 185, 129, 0.2);
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-1px);
            background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
            box-shadow: 0 4px 15px rgba(16, 185, 129, 0.15);
        }

        .sw-icon {
            font-size: 0.9em;
            color: #10b981;
            animation: none;
        }

        span {
            text-transform: uppercase;
            letter-spacing: 0.03em;
            white-space: nowrap;
        }
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.8; }
    }

    .checkout_content {
        display: grid;
        grid-template-columns: 1fr 1.5fr;
        gap: 3em;
        align-items: start;
    }

    /* Summary Styles */
    .checkout_summary {
        position: sticky;
        top: 2em;
    }
    
    
    
    @keyframes starTwinkle {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.2); }
    }

    .checkoutSummary {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
        border-radius: 2em;
        padding: 2.5em;
        box-shadow: 
            0 20px 25px -5px rgba(0, 0, 0, 0.08),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(10px);
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #10b981 100%);
            border-radius: 2em 2em 0 0;
        }
    }

    .checkoutSummary_header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2em;
        padding-bottom: 1.5em;
        border-bottom: 2px solid #f1f5f9;
    }

    .checkoutSummary_title {
        font-size: 1.5em;
        font-weight: 800;
        background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5em;

        &::after {
            content: '✨';
            font-size: 0.8em;
        }
    }

    .checkoutSummary_badge {
        padding: 0.75em 1.5em;
        border-radius: 2em;
        font-size: 0.75em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        position: relative;
        overflow: hidden;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
            transition: left 0.5s;
        }

        &:hover::before {
            left: 100%;
        }

        &--essential {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            color: white;
        }

        &--enterprise {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: white;
            
            &::after {
                content: '👑';
                margin-left: 0.5em;
            }
        }
    }

    /* Plan Hero Section - Compacto e Elegante */
    .checkoutSummary_planHero {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 1.25em;
        padding: 1.5em;
        margin-bottom: 1.5em;
        color: white;
        position: relative;
        overflow: hidden;
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
        
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
    }
    
    .planHero_content {
        position: relative;
        z-index: 1;
    }

    .planHero_billingToggle {
        display: flex;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 0.75em;
        padding: 0.25em;
        margin-bottom: 1.5em;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .billingToggle_btn {
        flex: 1;
        padding: 0.75em 1em;
        background: transparent;
        border: none;
        border-radius: 0.5em;
        color: rgba(255, 255, 255, 0.7);
        font-weight: 600;
        font-size: 0.875em;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5em;

        &.active {
            background: rgba(255, 255, 255, 0.2);
            color: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        &:hover:not(.active) {
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.9);
        }
    }

    .billingToggle_discount {
        background: rgba(16, 185, 129, 0.2);
        color: rgba(220, 252, 231, 0.95);
        padding: 0.2em 0.4em;
        border-radius: 0.4em;
        font-size: 0.75em;
        font-weight: 700;
        border: 1px solid rgba(16, 185, 129, 0.3);
    }
    
    .planHero_header {
        display: flex;
        align-items: center;
        gap: 1.25em;
        margin-bottom: 1.25em;
    }
    
    .planHero_icon {
        width: 3em;
        height: 3em;
        background: rgba(255, 255, 255, 0.12);
        border-radius: 0.875em;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25em;
        flex-shrink: 0;
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    }
    
    .planHero_info {
        flex: 1;
        min-width: 0;
    }
    
    .planHero_badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35em;
        background: rgba(255, 255, 255, 0.15);
        color: rgba(255, 255, 255, 0.95);
        padding: 0.25em 0.65em;
        border-radius: 0.75em;
        font-size: 0.7em;
        font-weight: 600;
        margin-bottom: 0.4em;
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        text-transform: uppercase;
        letter-spacing: 0.03em;
        
        .sw-icon {
            font-size: 0.9em;
        }
    }
    
    .planHero_name {
        font-size: 1.3em;
        font-weight: 700;
        margin: 0 0 0.2em 0;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        line-height: 1.2;
    }
    
    .planHero_description {
        font-size: 0.85em;
        margin: 0;
        opacity: 0.85;
        line-height: 1.3;
    }
    
    .planHero_pricing {
        padding: 1.25em;
        background: rgba(255, 255, 255, 0.08);
        border-radius: 0.875em;
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.12);
        margin-bottom: 0.75em;
    }
    
    .planHero_pricingHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    
    .planHero_pricingLabel {
        display: flex;
        align-items: center;
        gap: 0.4em;
        font-size: 0.7em;
        font-weight: 500;
        opacity: 0.75;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        
        .sw-icon {
            font-size: 1em;
        }
    }
    
    .planHero_price {
        display: flex;
        align-items: baseline;
        gap: 0.2em;
    }
    
    .planHero_yearlyDetails {
        display: flex;
        flex-direction: column;
        gap: 0.5em;
        padding-top: 0.75em;
        border-top: 1px solid rgba(255, 255, 255, 0.12);
    }
    
    .planHero_currency {
        font-size: 0.9em;
        font-weight: 600;
        opacity: 0.85;
    }
    
    .planHero_amount {
        font-size: 2em;
        font-weight: 800;
        line-height: 1;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    
    .planHero_period {
        font-size: 0.75em;
        font-weight: 500;
        opacity: 0.75;
        margin-left: 0.2em;
    }
    
    
    .planHero_extraResources {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75em;
        background: rgba(249, 115, 22, 0.15);
        border-radius: 0.75em;
        border: 1px solid rgba(249, 115, 22, 0.2);
        backdrop-filter: blur(8px);
        margin-bottom: 0.75em;
    }

    .extraResources_label {
        display: flex;
        align-items: center;
        gap: 0.4em;
        font-size: 0.75em;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.9);
        
        .sw-icon {
            font-size: 0.9em;
        }
    }

    .extraResources_cost {
        font-size: 0.875em;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.95);
        background: rgba(255, 255, 255, 0.15);
        padding: 0.25em 0.5em;
        border-radius: 0.5em;
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .planHero_discount {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.4em;
        background: rgba(16, 185, 129, 0.15);
        color: rgba(220, 252, 231, 0.95);
        padding: 0.5em 0.8em;
        border-radius: 0.75em;
        font-size: 0.75em;
        font-weight: 500;
        backdrop-filter: blur(8px);
        border: 1px solid rgba(16, 185, 129, 0.2);
        
        .sw-icon {
            font-size: 0.9em;
        }
        
        span {
            strong {
                font-weight: 700;
                color: rgba(255, 255, 255, 0.95);
            }
        }
    }
    
    .planHero_commitment {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.4em;
        font-size: 0.75em;
        font-weight: 500;
        opacity: 0.75;
        padding: 0.6em 1.2em;
        background: rgba(255, 255, 255, 0.08);
        border-radius: 0.75em;
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.12);
        
        .sw-icon {
            font-size: 0.9em;
            opacity: 0.9;
        }
    }

    .checkoutSummary_features {
        margin-bottom: 2em;
    }

    .checkoutSummary_featuresTitle {
        font-size: 0.875em;
        font-weight: 600;
        color: #374151;
        margin-bottom: 1em;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .checkoutSummary_features ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .checkoutSummary_features li {
        display: flex;
        align-items: center;
        gap: 0.75em;
        padding: 0.625em 0;
        font-size: 0.9em;
        color: #475569;

        .sw-icon {
            color: #10b981;
            font-size: 1.125em;
        }
    }

    // Features detalhadas
    .checkoutSummary_featureItem {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;
        margin-bottom: 1em;
        padding: 0.75em;
        border-radius: 0.5em;
        transition: all 0.2s ease;

        &:hover {
            background-color: #f9fafb;
        }

        &--disabled {
            opacity: 0.5;
            
            .checkoutSummary_featureName {
                text-decoration: line-through;
            }
        }

        &--highlight {
            background-color: rgba(102, 126, 234, 0.05);
            border: 1px solid rgba(102, 126, 234, 0.15);
            padding: 12px !important;
            
            .checkoutSummary_featureName {
                color: #667eea;
                font-weight: 600;
            }

            .checkoutSummary_featureIcon {
                background-color: #667eea;
                color: white;
            }
        }
    }

    .checkoutSummary_featureIcon {
        width: 2em;
        height: 2em;
        background-color: #10b981;
        color: white;
        border-radius: 0.375em;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875em;
        flex-shrink: 0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

        &--disabled {
            background-color: #ef4444;
        }
    }

    .checkoutSummary_featureContent {
        flex: 1;
        min-width: 0;
    }

    .checkoutSummary_featureName {
        font-size: 0.9375em;
        font-weight: 500;
        color: #374151;
        display: block;
        margin-bottom: 0.25em;
        line-height: 1.3;
    }

    .checkoutSummary_featureDescription {
        font-size: 0.8125em;
        color: #6b7280;
        line-height: 1.4;
        margin: 0;
    }


    /* Form Styles */
    .checkout_form {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
        border-radius: 2em;
        padding: 3em;
        box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.08),
            0 10px 25px -5px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(10px);
        position: relative;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 
                0 30px 60px -12px rgba(0, 0, 0, 0.12),
                0 15px 30px -5px rgba(0, 0, 0, 0.06),
                inset 0 1px 0 rgba(255, 255, 255, 0.9);
            transform: translateY(-2px);
        }

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border-radius: 2em 2em 0 0;
        }

        &::after {
            content: '🔒';
            position: absolute;
            top: 1.5em;
            right: 2em;
            font-size: 1.5em;
            opacity: 0.6;
            animation: float 3s ease-in-out infinite;
        }
    }

    @keyframes shimmer {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
    }

    @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-5px); }
    }

    .checkoutForm_title {
        font-size: 1.75em;
        font-weight: 700;
        background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin: 0 0 2em 0;
        display: flex;
        align-items: center;
        gap: 0.75em;
        animation: slideInDown 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        position: relative;

        .checkoutForm_titleIcon {
            width: 2.5em;
            height: 2.5em;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
            color: white;
            font-size: 1.2em;
            animation: rotateIn 0.8s ease-out 0.3s both;
            transition: all 0.3s ease;
            
            &:hover {
                transform: rotate(360deg) scale(1.1);
                box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
            }
        }

        .checkoutForm_titleSparkle {
            position: absolute;
            right: -0.5em;
            top: -0.25em;
            font-size: 0.6em;
            animation: sparkle 2s ease-in-out infinite;
        }
    }

    @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
        50% { opacity: 0.7; transform: scale(1.2) rotate(180deg); }
    }

    .checkoutForm_section {
        margin-bottom: 2.5em;
    }

    .checkoutForm_sectionTitle {
        display: flex;
        align-items: center;
        gap: 0.75em;
        font-size: 1.125em;
        font-weight: 600;
        color: #334155;
        margin-bottom: 1.25em;
        padding: 0.75em 0;
        border-bottom: 2px solid rgba(102, 126, 234, 0.1);
        position: relative;

        &::before {
            content: '🛡️';
            font-size: 0.9em;
            padding: 0.3em;
            background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
            border-radius: 0.5em;
            border: 1px solid rgba(59, 130, 246, 0.2);
        }

        &::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 15%;
            height: 2px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 1px;
            transition: width 0.3s ease;
        }

        &:hover::after {
            width: 30%;
        }
    }

    .checkoutForm_grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5em;
    }

    .checkoutForm_field {
        &--full {
            grid-column: 1 / -1;
        }
    }


    // Adição de ícone na seção título
    .section_icon {
        margin-right: 0.5em;
        color: #667eea;
    }

    /* ===== PREMIUM CARD STYLES - NEW DESIGN ===== */
    
    .credit_card_preview {
        margin: 2.5em 0;
        display: flex;
        justify-content: center;
        perspective: 1200px;
    }

    .credit_card {
        width: 380px;
        height: 240px;
        border-radius: 20px;
        position: relative;
        transform-style: preserve-3d;
        cursor: pointer;
        transition: all 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        
        &:hover:not(.flipped) {
            transform: rotateY(-8deg) rotateX(5deg) translateY(-12px) scale(1.02);
        }
        
        &.flipped {
            transform: rotateY(180deg);
        }
    }

    .credit_card_front,
    .credit_card_back {
        position: absolute;
        width: 100%;
        height: 100%;
        backface-visibility: hidden;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.25),
            0 10px 30px rgba(0, 0, 0, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }

    .credit_card_front {
        z-index: 2;
    }

    .credit_card_back {
        transform: rotateY(180deg);
        z-index: 1;
    }

    /* ===== FRONT CARD DESIGN ===== */
    
    .card_gradient {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, 
            #667eea 0%, 
            #764ba2 25%, 
            #667eea 50%, 
            #f093fb 75%, 
            #f5576c 100%);
        background-size: 300% 300%;
        animation: gradientShift 8s ease infinite;
    }

    .card_overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(45deg, 
            rgba(0, 0, 0, 0.1) 0%, 
            transparent 30%, 
            rgba(255, 255, 255, 0.1) 100%);
    }

    .card_pattern {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }

    .pattern_circle {
        position: absolute;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
        
        &.circle1 {
            width: 120px;
            height: 120px;
            top: -40px;
            right: -30px;
            animation: float1 6s ease-in-out infinite;
        }
        
        &.circle2 {
            width: 80px;
            height: 80px;
            bottom: -20px;
            left: -20px;
            animation: float2 8s ease-in-out infinite;
        }
        
        &.circle3 {
            width: 60px;
            height: 60px;
            top: 50%;
            left: 30%;
            animation: float3 10s ease-in-out infinite;
        }
    }

    .card_content {
        position: relative;
        z-index: 10;
        padding: 1.8em;
        height: 100%;
        display: flex;
        flex-direction: column;
        color: white;
    }

    .card_header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.2em;
    }

    .card_brand {
        font-size: 1.1em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        
        &.visa {
            color: #1a1f71;
            background: white;
            padding: 0.3em 0.8em;
            border-radius: 8px;
            font-style: italic;
        }
        
        &.mastercard {
            color: #ff5f00;
            font-weight: 900;
        }
        
        &.elo {
            color: #ffcd00;
            font-weight: 800;
        }
    }

    .card_type {
        font-size: 0.7em;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        letter-spacing: 2px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .card_chip_premium {
        width: 40px;
        height: 32px;
        background: linear-gradient(145deg, #d4af37, #ffd700, #d4af37);
        border-radius: 8px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        margin-bottom: 1.5em;
        position: relative;
        box-shadow: 
            0 4px 8px rgba(0, 0, 0, 0.3),
            inset 0 2px 4px rgba(255, 255, 255, 0.2);
    }

    .chip_inner {
        position: absolute;
        top: 4px;
        left: 4px;
        right: 4px;
        bottom: 4px;
        background: linear-gradient(45deg, #b8860b, #daa520);
        border-radius: 4px;
        overflow: hidden;
    }

    .chip_reflection {
        position: absolute;
        top: -2px;
        left: -2px;
        width: 50%;
        height: 50%;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.6), transparent);
        border-radius: 4px;
    }

    .card_number_premium {
        font-size: 1.4em;
        font-weight: 600;
        letter-spacing: 3px;
        margin-bottom: 1.5em;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
        font-family: 'Courier New', monospace;
    }

    .card_info {
        display: flex;
        justify-content: space-between;
        margin-top: auto;
    }

    .card_holder_info,
    .card_expiry_info {
        flex: 1;
    }

    .info_label {
        font-size: 0.65em;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.8);
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 0.3em;
    }

    .info_value {
        font-size: 0.9em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    }

    .security_elements {
        position: absolute;
        bottom: 15px;
        right: 15px;
        z-index: 5;
    }

    .hologram {
        width: 30px;
        height: 30px;
        background: linear-gradient(45deg, 
            transparent, 
            rgba(255, 255, 255, 0.1), 
            transparent, 
            rgba(255, 255, 255, 0.2), 
            transparent);
        background-size: 15px 15px;
        border-radius: 50%;
        animation: shimmer 3s ease-in-out infinite;
    }

    /* ===== BACK CARD DESIGN ===== */
    
    .card_gradient_back {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, 
            #434343 0%, 
            #000000 50%, 
            #434343 100%);
    }

    .card_content_back {
        position: relative;
        z-index: 2;
        height: 100%;
        color: white;
        padding: 1.5em;
    }

    .magnetic_stripe_premium {
        position: absolute;
        top: 25px;
        left: 0;
        right: 0;
        height: 40px;
        background: linear-gradient(to right, #2d2d2d, #1a1a1a, #2d2d2d);
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5);
    }

    .signature_area {
        margin-top: 4em;
        margin-bottom: 1.5em;
    }

    .signature_label {
        font-size: 0.7em;
        color: #ccc;
        margin-bottom: 0.5em;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .signature_box {
        background: white;
        height: 35px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        padding: 0 1em;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .signature_text {
        color: #2d3748;
        font-style: italic;
        font-size: 0.9em;
    }

    .cvv_section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5em;
    }

    .cvv_container {
        display: flex;
        align-items: center;
        gap: 0.8em;
    }

    .cvv_label {
        font-size: 0.8em;
        font-weight: 600;
        color: #ccc;
        text-transform: uppercase;
        letter-spacing: 1px;
    }

    .cvv_value {
        background: white;
        color: #2d3748;
        padding: 0.5em 1em;
        border-radius: 6px;
        font-weight: 700;
        font-family: 'Courier New', monospace;
        letter-spacing: 2px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .cvv_info {
        text-align: right;
        
        p {
            font-size: 0.65em;
            color: #aaa;
            margin: 0.2em 0;
            line-height: 1.3;
        }
    }

    .bank_info {
        margin-bottom: 1em;
    }

    .bank_logo {
        font-size: 1em;
        font-weight: 700;
        color: #667eea;
        margin-bottom: 0.3em;
        text-transform: uppercase;
        letter-spacing: 1px;
    }

    .bank_contact {
        font-size: 0.7em;
        color: #aaa;
    }

    .security_seals {
        display: flex;
        gap: 0.8em;
        justify-content: center;
    }

    .seal {
        background: rgba(255, 255, 255, 0.1);
        padding: 0.3em 0.6em;
        border-radius: 12px;
        font-size: 0.65em;
        font-weight: 600;
        color: #ccc;
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    /* ===== KEYFRAME ANIMATIONS ===== */
    
    @keyframes slideInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes slideInDown {
        from {
            opacity: 0;
            transform: translateY(-30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes slideInLeft {
        from {
            opacity: 0;
            transform: translateX(-30px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @keyframes slideInRight {
        from {
            opacity: 0;
            transform: translateX(30px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes fadeInRight {
        from {
            opacity: 0;
            transform: translateX(20px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @keyframes fadeInScale {
        from {
            opacity: 0;
            transform: scale(0.9);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    // === CLASSES CSS FALTANTES ===

    .checkout_background_pattern {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        opacity: 0.3;
    }

    .checkout_loading_content {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        
        h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--textPrimary, #1f2937);
            margin: 0;
        }
        
        p {
            color: var(--textSecondary, #6b7280);
            margin: 0;
        }
    }

    .planHero_background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 1rem;
        overflow: hidden;
        z-index: 0;
    }

    .planHero_backgroundPattern {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
        background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }

    .planHero_iconGlow {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 120%;
        height: 120%;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
        border-radius: 50%;
        z-index: -1;
    }

    // Modal Classes
    .modalHeader {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1.5rem;
    }

    .modalHeader_icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        border-radius: 0.75rem;
        color: white;
        font-size: 1.5rem;
    }

    .modalHeader_title {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .modalContent {
        padding: 0;
        margin-bottom: 2rem;
    }

    .modalActions {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        padding-top: 1.5rem;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
        margin-top: 1.5rem;
    }

    .successContent {
        text-align: center;
        padding: 2rem;
    }

    @keyframes rotateIn {
        from {
            opacity: 0;
            transform: rotate(-200deg) scale(0.8);
        }
        to {
            opacity: 1;
            transform: rotate(0deg) scale(1);
        }
    }

    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.05);
            opacity: 0.8;
        }
    }

    @keyframes bounce {
        0%, 20%, 53%, 80%, 100% {
            transform: translateY(0);
        }
        40%, 43% {
            transform: translateY(-8px);
        }
        70% {
            transform: translateY(-4px);
        }
        90% {
            transform: translateY(-2px);
        }
    }

    @keyframes gradientMove {
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    }

    /* ===== NEW CARD ANIMATIONS ===== */

    @keyframes gradientShift {
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    }

    @keyframes float1 {
        0%, 100% {
            transform: translate(0px, 0px) scale(1);
        }
        33% {
            transform: translate(10px, -10px) scale(1.05);
        }
        66% {
            transform: translate(-5px, 5px) scale(0.95);
        }
    }

    @keyframes float2 {
        0%, 100% {
            transform: translate(0px, 0px) scale(1);
        }
        50% {
            transform: translate(15px, -15px) scale(1.1);
        }
    }

    @keyframes float3 {
        0%, 100% {
            transform: translate(0px, 0px) rotate(0deg) scale(1);
        }
        25% {
            transform: translate(5px, -8px) rotate(90deg) scale(1.1);
        }
        50% {
            transform: translate(-8px, -5px) rotate(180deg) scale(0.9);
        }
        75% {
            transform: translate(-5px, 8px) rotate(270deg) scale(1.05);
        }
    }

    /* =======================
       CHECKOUT STEPS SYSTEM
       ======================= */
    .checkout_steps {
        margin: 2em 0 3em;
        padding: 2em;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.9));
        border: 1px solid rgba(229, 231, 235, 0.6);
        border-radius: 1em;
        backdrop-filter: blur(10px);
    }

    .checkout_steps_container {
        display: flex;
        align-items: center;
        justify-content: center;
        max-width: 500px;
        margin: 0 auto;
    }

    .checkout_step {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75em;
        opacity: 0.5;
        transition: all 0.4s ease;
        position: relative;

        &--active {
            opacity: 1;
            
            .checkout_step_circle {
                background: linear-gradient(135deg, var(--colorBlue, #3B82F6), var(--colorPurple, #8B5CF6));
                color: white;
                box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
                transform: scale(1.1);
            }

            .checkout_step_label {
                color: var(--colorBlue, #3B82F6);
                font-weight: 600;
            }
        }

        &--completed {
            .checkout_step_circle {
                background: linear-gradient(135deg, var(--colorGreen, #10B981), var(--colorBlue, #3B82F6));
                animation: completedPulse 2s ease-in-out;
            }
        }
    }

    .checkout_step_circle {
        width: 3em;
        height: 3em;
        border-radius: 50%;
        background: rgba(156, 163, 175, 0.2);
        border: 2px solid rgba(156, 163, 175, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1.125em;
        color: rgba(107, 114, 128, 0.8);
        transition: all 0.4s ease;
        position: relative;
        z-index: 2;

        sw-icon {
            width: 1.25em;
            height: 1.25em;
        }
    }

    .checkout_step_label {
        font-size: 0.875em;
        font-weight: 500;
        color: rgba(107, 114, 128, 0.8);
        text-align: center;
        transition: all 0.4s ease;
        white-space: nowrap;
    }

    .checkout_steps_divider {
        flex: 1;
        height: 2px;
        background: rgba(156, 163, 175, 0.3);
        margin: 0 1em;
        border-radius: 1px;
        position: relative;
        overflow: hidden;
        max-width: 120px;

        &--active {
            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%;
                background: linear-gradient(90deg, var(--colorBlue, #3B82F6), var(--colorPurple, #8B5CF6));
                animation: progressFill 0.6s ease-out;
            }
        }
    }

    /* Step Content */
    .checkout_step_content {
        animation: stepFadeIn 0.5s ease-out;
    }

    .checkout_info_note {
        display: flex;
        align-items: center;
        gap: 0.875em;
        margin-bottom: 2em;
        padding: 1.25em 1.5em;
        background: linear-gradient(135deg, 
            rgba(59, 130, 246, 0.08) 0%, 
            rgba(139, 92, 246, 0.06) 100%);
        border: 1px solid rgba(59, 130, 246, 0.15);
        border-radius: 0.875em;
        color: rgba(107, 114, 128, 0.9);
        font-size: 0.9375em;
        font-weight: 500;
        position: relative;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 
            0 2px 8px rgba(59, 130, 246, 0.08),
            0 1px 3px rgba(0, 0, 0, 0.05);

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            background: linear-gradient(180deg, 
                var(--colorBlue, #3B82F6), 
                var(--colorPurple, #8B5CF6));
        }

        &:hover {
            transform: translateY(-1px);
            box-shadow: 
                0 4px 12px rgba(59, 130, 246, 0.12),
                0 2px 6px rgba(0, 0, 0, 0.08);
            border-color: rgba(59, 130, 246, 0.2);
        }

        sw-icon {
            width: 1.375em;
            height: 1.375em;
            color: var(--colorBlue, #3B82F6);
            opacity: 0.9;
            animation: iconGlow 3s ease-in-out infinite;
            flex-shrink: 0;
        }

        span {
            color: rgba(51, 65, 85, 0.95);
            line-height: 1.5;
            font-weight: 500;
        }
    }


    /* Step Actions */
    .checkout_step_actions {
        display: flex;
        justify-content: space-between;
        gap: 1em;
        margin-top: 2.5em;
        padding-top: 2em;
        border-top: 1px solid rgba(229, 231, 235, 0.6);

        .checkout_next_btn,
        .checkout_payment_btn {
            flex: 1;
            max-width: none;
            height: 3.5em;
            font-weight: 600;
            border-radius: 0.75em;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                transition: left 0.5s ease;
            }

            &:hover::before {
                left: 100%;
            }

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(59, 130, 246, 0.25);
            }
        }

        .checkout_prev_btn {
            flex: 0 0 auto;
            min-width: 120px;
        }
    }

    /* Animações */
    @keyframes stepFadeIn {
        from {
            opacity: 0;
            transform: translateX(1em);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @keyframes completedPulse {
        0% {
            box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }
        50% {
            box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
            transform: scale(1.15);
        }
        100% {
            box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
            transform: scale(1.1);
        }
    }

    @keyframes progressFill {
        from {
            width: 0%;
        }
        to {
            width: 100%;
        }
    }

    @keyframes iconFloat {
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-0.25em);
        }
    }

    @keyframes iconGlow {
        0%, 100% {
            opacity: 0.9;
            transform: scale(1);
        }
        50% {
            opacity: 1;
            transform: scale(1.05);
        }
    }

    /* Responsividade */
    @import '@/assets/css/mediaScreen.scss';

    @include screen_medias(s, m) {
        .checkout_steps {
            padding: 1.5em;
            margin: 1.5em 0 2em;
        }

        .checkout_steps_container {
            max-width: 400px;
        }

        .checkout_step_circle {
            width: 2.5em;
            height: 2.5em;
            font-size: 1em;
        }

        .checkout_step_label {
            font-size: 0.8125em;
        }

        .checkout_steps_divider {
            max-width: 80px;
            margin: 0 0.75em;
        }

        .checkout_info_note {
            padding: 1em 1.25em;
            font-size: 0.875em;
            margin-bottom: 1.5em;
            border-radius: 0.75em;
            gap: 0.75em;

            &::before {
                width: 3px;
            }

            sw-icon {
                width: 1.25em;
                height: 1.25em;
            }

            span {
                font-size: 0.875em;
            }
        }

        .checkout_step_actions {
            flex-direction: column;
            gap: 1em;

            .checkout_prev_btn,
            .checkout_next_btn,
            .checkout_payment_btn {
                width: 100%;
                max-width: none;
            }
        }
    }

    @include screen_s {
        .checkout_step_label {
            font-size: 0.75em;
        }

        .checkout_steps_divider {
            max-width: 60px;
            margin: 0 0.5em;
        }
    }

</style>